import type {
  HealthTheme,
  HealthThemeColor,
  RecordBlock,
  RecordQuestionType,
} from '@hue/contracts';
import type { ClientChart, ClinicalDocumentSummary, SessionMaterialLine } from '../../lib/api.js';

/// Os oito tipos de resposta como quem monta o modelo os chama (ADR 0038, decisão 3).
export const questionTypes: Record<RecordQuestionType, { label: string; hint: string }> = {
  SHORT_TEXT: { label: 'Texto curto', hint: 'Uma linha: nome de produto, marca, medida.' },
  LONG_TEXT: { label: 'Texto longo formatado', hint: 'Parágrafos com negrito, itálico e lista.' },
  YES_NO: { label: 'Sim ou não', hint: 'Com detalhe opcional quando a resposta é Sim.' },
  SINGLE_CHOICE: { label: 'Escolha única', hint: 'Uma opção da lista.' },
  MULTIPLE_CHOICE: { label: 'Caixas de marcação', hint: 'Várias opções da lista.' },
  DECLARATION: { label: 'Declaração', hint: 'Uma caixa que a cliente marca: “Declaro que…”.' },
  NUMBER: { label: 'Número', hint: 'Quantidade ou medida.' },
  DATE: { label: 'Data', hint: 'Dia, mês e ano.' },
};

/// Junta classes, pulando as condições falsas.
export function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

export const themeColors: Record<HealthThemeColor, string> = {
  danger: 'Vermelho',
  warning: 'Âmbar',
  progress: 'Azul',
  brand: 'Dourado',
  positive: 'Verde',
  neutral: 'Cinza',
};

/// O tema pelo que a pergunta guarda. Tema excluído não existe — só se exclui o que nenhuma
/// pergunta cita —, mas a tela não quebra se o dado chegar torto.
export function themeOf(themes: readonly HealthTheme[], key: string): HealthTheme {
  return (
    themes.find((theme) => theme.key === key) ?? {
      key,
      displayName: 'Tema removido',
      color: 'neutral',
      flagsDivergence: false,
      system: false,
      active: false,
    }
  );
}

export const blockKinds: Record<RecordBlock['kind'], string> = {
  HEADING: 'Título',
  SUBHEADING: 'Subtítulo',
  DESCRIPTION: 'Descrição',
  QUESTION: 'Pergunta',
};

export function newBlock(kind: RecordBlock['kind']): RecordBlock {
  const id = crypto.randomUUID();
  if (kind === 'HEADING' || kind === 'SUBHEADING') return { id, kind, text: '' };
  if (kind === 'DESCRIPTION') return { id, kind, html: '' };
  return {
    id,
    kind,
    label: '',
    help: null,
    type: 'YES_NO',
    required: false,
    options: [],
    exclusiveOptionId: null,
    detailLabel: null,
    themeKey: null,
    flaggedOptionIds: [],
  };
}

export function chartQueryKey(clientId: string) {
  return ['company', 'records', 'chart', clientId] as const;
}

export function blocksOf(chart: ClientChart, templateVersionId: string): RecordBlock[] {
  return chart.templateVersions.find((version) => version.id === templateVersionId)!.blocks;
}

/// Nome de arquivo sem acento nem espaço, igual ao que o servidor dá: "ficha-coloracao-ana-v2.pdf".
export function pdfName(...parts: string[]): string {
  const slug = parts
    .join(' ')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, '-')
    .replace(/^-|-$/gu, '')
    .slice(0, 100);
  return `${slug}.pdf`;
}

/// Entrega o arquivo gerado no servidor para a pessoa salvar.
export function saveFile(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

/// Os documentos que incluem uma versão: o da ficha e os do prontuário (ADR 0040, decisão 9).
export function documentsOf(
  documents: readonly ClinicalDocumentSummary[],
  versionId: string,
): ClinicalDocumentSummary[] {
  return documents.filter((document) => document.versionIds.includes(versionId));
}

const UNITS: Record<string, string> = { GRAM: 'g', MILLILITER: 'ml', UNIT: 'un' };

export function materialLine(line: SessionMaterialLine): string {
  return `${line.displayName} — ${line.quantity.replace('.', ',')} ${UNITS[line.baseUnit] ?? line.baseUnit}`;
}

/// As fichas que valem para o serviço do atendimento, a do próprio serviço antes das que valem para
/// todos: é ela que fica em destaque (ADR 0038, adendo de 2026-09-28).
export function serviceTemplatesOf(chart: ClientChart, serviceId: string) {
  const active = chart.templates.filter((template) => template.active);
  return [
    ...active.filter(
      (template) => !template.allServices && template.serviceIds.includes(serviceId),
    ),
    ...active.filter((template) => template.allServices),
  ];
}
